import { z } from "zod";
import {
  AgentStreamResponse,
  agentStreamResponseSchema,
} from "@altimateai/ui-components/chatbotV2";

// Session Status Enum (reflects the conversation/turn state, not lifecycle)
export const sessionStatusSchema = z.enum(["failed", "completed", "pending", "cancelled"]);
export type SessionStatusEnum = z.infer<typeof sessionStatusSchema>;

// Lifecycle status (active / archived / deleted) — controls whether a
// session is shown in the sidebar at all. The transitions are
// user-driven only: archived sessions stay archived indefinitely
// until the user explicitly deletes them. There is no automatic
// archived → deleted promotion. ``deleted`` rows stay in the DB
// (a separate backend pipeline handles eventual hard removal) but
// are hidden from every FE listing.
export const sessionLifecycleStatusSchema = z.enum(["active", "archived", "deleted"]);
export type SessionLifecycleStatus = z.infer<typeof sessionLifecycleStatusSchema>;

// Filter accepted by GET /studio/sessions ?status=
export const sessionLifecycleListFilterSchema = z.enum(["active", "archived", "all"]);
export type SessionLifecycleListFilter = z.infer<typeof sessionLifecycleListFilterSchema>;

// Playground Session Types
export const playgroundSessionSchema = z.object({
  session_id: z.string(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  status: sessionStatusSchema,
  datamates: z.array(z.coerce.string()).optional().nullable(),
  knowledge: z.array(z.string()).optional().nullable(),
  context: z.record(z.unknown()).optional().nullable(),
  // One-line intent summary for the conversation — lazily generated by
  // the backend (Haiku). NULL/undefined means "not generated yet";
  // sidebar falls back to `name` (original question). Named
  // `session_intent` to avoid confusion with the per-turn `summary`
  // field on chat blocks.
  session_intent: z.string().optional().nullable(),
});

// Status-update response from PATCH /studio/sessions/{id}
export const sessionStatusUpdateResponseSchema = z.object({
  session_id: z.string(),
  status: sessionLifecycleStatusSchema,
});
export type SessionStatusUpdateResponse = z.infer<typeof sessionStatusUpdateResponseSchema>;

export type PlaygroundSession = z.infer<typeof playgroundSessionSchema>;

export const playgroundAllSessionResponseSchema = z.object({
  sessions: z.array(playgroundSessionSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  has_more: z.boolean(),
});

export type PlaygroundAllSessionResponse = z.infer<typeof playgroundAllSessionResponseSchema>;

// Attachment Schema
export const attachmentSchema = z.object({
  id: z.string(),
  attachment_name: z.string(),
  file_type: z.string().nullable(),
  s3_url: z.string().nullable(),
  extracted_content: z.string().nullable(),
  order: z.number(),
  attachment_type: z.string(),
  attachment_reference_id: z.string().nullable(),
  file_size: z.number().nullable(),
  created_on: z.string().transform(val => val), // Keep as ISO string for now
});

// Chat Block Types
export const chatBlockSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.array(agentStreamResponseSchema),
  id: z.coerce.string(),
  // transform string to epoch timestamp
  timestamp: z
    .string()
    .optional()
    .transform(val => {
      if (val) {
        const date = new Date(val);
        return date.getTime();
      }
      return Date.now(); // Fallback to current time if not provided
    }),
  attachments: z.array(attachmentSchema).optional().default([]),
});

export type ChatBlock = z.infer<typeof chatBlockSchema>;

// Playground Session Details Response
export const playgroundSessionDetailsResponseSchema = z.object({
  session: playgroundSessionSchema,
  turns: z.array(chatBlockSchema),
});

export type PlaygroundSessionDetailsResponse = z.infer<
  typeof playgroundSessionDetailsResponseSchema
>;

// TypeScript interfaces with proper typing (for runtime use)
export interface ChatBlockTyped {
  id: number;
  role: "user" | "assistant";
  content: AgentStreamResponse[];
}

export interface PlaygroundSessionDetailsResponseTyped {
  session: PlaygroundSession;
  turns: ChatBlockTyped[];
}

// Legacy interface for backward compatibility
export interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: string;
  messageCount: number;
}

// ========== Share Links (US-003) ==========

export const shareAccessTypeSchema = z.enum([
  "anyone",
  "specific_people",
  "all_altimate_users",
  "team",
]);
export type ShareAccessType = z.infer<typeof shareAccessTypeSchema>;

export const shareLinkSchema = z.object({
  id: z.string().uuid(),
  session_id: z.string(),
  access_type: shareAccessTypeSchema,
  expires_at: z.string().nullable(),
  created_by: z.number(),
  created_at: z.string(),
  title: z.string().nullable(),
  share_url: z.string(),
  allowed_users: z.array(z.number()).nullable(),
  allowed_teams: z.array(z.number()).nullable().optional(),
});

export type ShareLink = z.infer<typeof shareLinkSchema>;

export const shareLinkResponseSchema = shareLinkSchema;
export type ShareLinkResponse = ShareLink;

export const shareLinkListResponseSchema = z.object({
  share_links: z.array(shareLinkSchema),
  total: z.number(),
});
export type ShareLinkListResponse = z.infer<typeof shareLinkListResponseSchema>;

export const sharedSessionResponseSchema = z.object({
  session_id: z.string(),
  session_name: z.string(),
  created_at: z.string(),
  share_title: z.string().nullable(),
  turns: z.array(chatBlockSchema),
  is_read_only: z.boolean(),
});
export type SharedSessionResponse = z.infer<typeof sharedSessionResponseSchema>;

export interface ShareLinkCreateRequest {
  session_id: string;
  access_type: ShareAccessType;
  expires_in_days?: number | null;
  user_ids?: number[];
  team_ids?: number[];
  title?: string;
}

// ========== Scheduled Reports (US-005) ==========

export const playgroundContentScopeSchema = z.enum(["first_query", "last_answer", "full_chat"]);
export type PlaygroundContentScope = z.infer<typeof playgroundContentScopeSchema>;

export const playgroundScheduleFrequencySchema = z.enum(["daily", "weekly", "monthly", "custom"]);
export type PlaygroundScheduleFrequency = z.infer<typeof playgroundScheduleFrequencySchema>;

export const playgroundScheduleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  session_id: z.string(),
  content_scope: playgroundContentScopeSchema,
  cron_expr: z.string(),
  destination_type: z.string(),
  emails: z.array(z.string()),
  slack_webhook_id: z.number().nullable().optional(),
  enabled: z.boolean(),
  last_run: z.string().nullable(),
  next_run: z.string().nullable(),
  created_by: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  // Derived from cron_expr by the backend so the FE can prefill the
  // day pickers without re-parsing the cron string. Optional for
  // backwards compatibility — older backends omit these fields and
  // the FE falls back to parsing cron_expr.
  day_of_week: z.number().nullable().optional(),
  day_of_month: z.number().nullable().optional(),
});

export type PlaygroundSchedule = z.infer<typeof playgroundScheduleSchema>;

export const playgroundScheduleResponseSchema = playgroundScheduleSchema;
export type PlaygroundScheduleResponse = PlaygroundSchedule;

export const playgroundScheduleListResponseSchema = z.object({
  schedules: z.array(playgroundScheduleSchema),
  total: z.number(),
});
export type PlaygroundScheduleListResponse = z.infer<typeof playgroundScheduleListResponseSchema>;

export const playgroundScheduleRunSchema = z.object({
  id: z.string().uuid(),
  schedule_id: z.string().uuid(),
  run_at: z.string(),
  status: z.string(),
  error_message: z.string().nullable(),
  recipients_count: z.number().nullable(),
  sent_count: z.number().nullable(),
  failed_count: z.number().nullable(),
});

export type PlaygroundScheduleRun = z.infer<typeof playgroundScheduleRunSchema>;

export const playgroundScheduleRunsListResponseSchema = z.object({
  runs: z.array(playgroundScheduleRunSchema),
  total: z.number(),
});
export type PlaygroundScheduleRunsListResponse = z.infer<
  typeof playgroundScheduleRunsListResponseSchema
>;

export interface PlaygroundScheduleCreateRequest {
  name: string;
  session_id: string;
  content_scope?: PlaygroundContentScope;
  frequency?: PlaygroundScheduleFrequency;
  custom_cron?: string;
  hour?: number;
  // Cron weekday (0=Sun … 6=Sat). Only meaningful for frequency='weekly'.
  day_of_week?: number;
  // Cron day-of-month (1-31). Only meaningful for frequency='monthly'.
  day_of_month?: number;
  emails?: string[];
  slack_webhook_id?: number;
}

export interface PlaygroundScheduleUpdateRequest {
  name?: string;
  content_scope?: PlaygroundContentScope;
  frequency?: PlaygroundScheduleFrequency;
  custom_cron?: string;
  hour?: number;
  day_of_week?: number;
  day_of_month?: number;
  emails?: string[];
  // 0 = clear the existing webhook; positive int = set; omitted = unchanged.
  slack_webhook_id?: number;
  enabled?: boolean;
}

// ========== Slack Webhook Sharing (US-004) ==========

export const shareToSlackResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().nullable().optional(),
});
export type ShareToSlackResponse = z.infer<typeof shareToSlackResponseSchema>;

export interface ShareToSlackRequest {
  session_id: string;
  webhook_id: number;
}
